import React, { useState } from 'react';
import {
  TouchableOpacity,
  Text,
  ActivityIndicator,
  StyleSheet,
  ViewStyle,
  TextStyle,
  Alert,
} from 'react-native';
import { useStripe } from '@stripe/stripe-react-native';
import { usePaymentsContext } from '../providers/PaymentsProvider';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateStripeId } from '../../shared/utils/validation';

interface CheckoutButtonProps {
  priceId: string;
  customerId?: string;
  customerEmail?: string;
  children: React.ReactNode;
  style?: ViewStyle | ViewStyle[];
  textStyle?: TextStyle | TextStyle[];
  disabled?: boolean;
  metadata?: Record<string, string>;
  trialPeriodDays?: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onLoading?: (loading: boolean) => void;
}

export const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  priceId,
  customerId,
  customerEmail,
  children,
  style,
  textStyle,
  disabled = false,
  metadata,
  trialPeriodDays,
  onSuccess,
  onError,
  onLoading,
}) => {
  const { initPaymentSheet, presentPaymentSheet } = useStripe();
  const { initialized } = usePaymentsContext();
  const [loading, setLoading] = useState(false);

  const handleError = (errorMessage: string) => {
    onError?.(errorMessage);
    Alert.alert('Payment Error', errorMessage);
    console.error('Checkout error:', errorMessage);
  };

  const handleCheckout = async () => {
    if (!initialized) {
      handleError(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
      return;
    }

    if (!validateStripeId(priceId, 'price')) {
      handleError(ERROR_MESSAGES.INVALID_PRICE_ID);
      return;
    }

    if (customerId && !validateStripeId(customerId, 'customer')) {
      handleError(ERROR_MESSAGES.INVALID_CUSTOMER_ID);
      return;
    }

    setLoading(true);
    onLoading?.(true);

    try {
      // Create subscription and setup intent on your server
      const response = await fetch('/api/payments/create-subscription-setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          customerId,
          customerEmail,
          metadata,
          trialPeriodDays,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || ERROR_MESSAGES.CHECKOUT_FAILED);
      }

      const {
        setupIntentClientSecret,
        customerId: returnedCustomerId,
        customerEphemeralKeySecret,
      } = await response.json();

      if (!setupIntentClientSecret) {
        throw new Error('No setup intent client secret returned from server');
      }

      // Initialize the payment sheet
      const { error: initError } = await initPaymentSheet({
        setupIntentClientSecret,
        customerId: returnedCustomerId,
        customerEphemeralKeySecret,
        merchantDisplayName: 'Your App Name', // TODO: Make this configurable
        allowsDelayedPaymentMethods: true,
      });

      if (initError) {
        throw new Error(
          initError.message || 'Failed to initialize payment sheet'
        );
      }

      // Present the payment sheet
      const { error: presentError } = await presentPaymentSheet();

      if (presentError) {
        // User cancelled or error occurred
        if (presentError.code !== 'Canceled') {
          throw new Error(
            presentError.message || ERROR_MESSAGES.PAYMENT_FAILED
          );
        }
        return; // User cancelled, don't show error
      }

      // Payment successful
      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : ERROR_MESSAGES.CHECKOUT_FAILED;
      handleError(errorMessage);
    } finally {
      setLoading(false);
      onLoading?.(false);
    }
  };

  const isDisabled = loading || disabled || !initialized;

  return (
    <TouchableOpacity
      style={[styles.button, style, isDisabled && styles.disabled]}
      onPress={handleCheckout}
      disabled={isDisabled}
      activeOpacity={0.8}
    >
      {loading ? (
        <ActivityIndicator color="#ffffff" size="small" style={styles.loader} />
      ) : null}
      <Text
        style={[styles.buttonText, textStyle, loading && styles.loadingText]}
      >
        {loading ? 'Processing...' : children}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  disabled: {
    opacity: 0.5,
  },
  loader: {
    marginRight: 8,
  },
  loadingText: {
    marginLeft: 8,
  },
});

export default CheckoutButton;
